import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtSearchBar from "../../components/NtSearchBar";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtListRow from "../../components/NtListRow";
import NtModal from "../../components/NtModal";
import NtText from "../../components/NtText";
import NtBadge from "../../components/NtBadge";
import NtPinItem from "../components/NtPinItem";
import NtPinCdrAdvanceSearchPicker from "../components/NtPinCdrAdvanceSearchPicker";
import { fetchCdrs } from "../../api/billing";
import {
  asyncStorageSetData,
  parseDecimalPlaces,
  parseDisplayDate,
} from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import BasePinController from "./BasePinController";
import cdrcsvcolumndata from "../data/cdrcsvcolumndata";
import pintypesdata from "../data/pintypesdata";
import terminationcausedata from "../data/terminationcausedata";
import { fetchCurrency } from "../utils/CurrencyParser";

class PinHistoryController extends BasePinController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    totalItems: 0,
    searchText: "",
    searchFilter: null,
    page: 1,
  };

  column_location = "@cdr_column_storage";
  advanceSearchRef = React.createRef();

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      cdrcsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.route.params !== this.props.route.params) {
      this.setState({ page: 1 }, () => this.handleDataFetch());
    }
  }


  handleDataFetch = async () => {
    this.setState({ loading: true });
    const response = await fetchCdrs(
      this.state.page,
      settings.pageSizeLoad,
      this.state.searchText,
      this.generateFullFilter(),
    );
    Logger("Cdrs fetch response", response);
    if (response.ok) {
      this.setState({
        data: response.data?.data || [],
        totalItems: response.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const response = await fetchCdrs(
      1,
      10000000,
      this.state.searchText,
      this.generateFullFilter(),
    );
    return response.ok ? response.data.data : [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "starttime" || column === "stoptime") {
      return `"${parseDisplayDate(item[column], "YYYY-MM-DD HH:mm:ss")}"`;
    }
    if (column === "answered") return item[column] == 1 ? "YES" : "NO";
    if (column === "sessiontime" || column === "billed_sessiontime") return item[column] / 60;
    if (["blockprice", "sessionprice", "blockcost", "sessioncost"].includes(column)) {
      return `${fetchCurrency(item.currency)?.symbol}${parseDecimalPlaces(item[column])}`;
    }
    if (column === "archived") return item[column] == 0 ? "False" : "True";
    if (column === "terminatecauseid") {
      return terminationcausedata?.find((each) => each.id == item[column])?.title || item[column];
    }
    return item[column];
  };

  handleTypeFetch = () =>
    pintypesdata.find((each) => each.id === this.props.route?.params?.type);

  handleSearch = (text) => {
    this.setState({ searchText: text, page: 1 }, () => this.handleDataFetch());
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  generateFullFilter = () => {
    let filter = this.state.searchFilter ? { ...this.state.searchFilter } : {};
    filter.type = this.props.route?.params?.type;
    return filter;
  };

  renderAdvanceSearchForm = () => (
    <NtModal ref={this.advanceSearchRef}>
      <NtPinCdrAdvanceSearchPicker
        type={this.props.route?.params?.type}
        filter={this.state.searchFilter}
        onCancel={() => this.advanceSearchRef.current?.dismiss()}
        onFilter={(filter) => {
          this.setState({ searchFilter: filter, page: 1 }, () => this.handleDataFetch());
          this.advanceSearchRef.current?.dismiss();
        }}
        onFilterClear={() => {
          this.setState({ searchFilter: null, page: 1 }, () => this.handleDataFetch());
          this.advanceSearchRef.current?.dismiss();
        }}
      />
    </NtModal>
  );

  renderRow = (item, index) => (
    <NtListRow
      key={item.id}
      item={item}
      columns={this.state.columns}
      renderCustom={(row, column) => {
        if (column === "pin") return <NtPinItem number={row[column]} showStatus={false} />;
        if (column === "starttime" || column === "stoptime") {
          return parseDisplayDate(row[column], "YYYY-MM-DD HH:mm:ss");
        }
        if (column === "callid") {
          return <NtText style={{ maxWidth: 100 }}>{row[column]}</NtText>;
        }
        if (column === "answered") return row[column] == 1 ? "YES" : "NO";
        if (column === "sessiontime" || column === "billed_sessiontime") return row[column] / 60;
        if (["blockprice", "sessionprice", "blockcost", "sessioncost"].includes(column)) {
          return `${fetchCurrency(row.currency)?.symbol}${parseDecimalPlaces(row[column])}`;
        }
        if (column === "archived") {
          const archived = row[column] != 0;
          return (
            <NtBadge variant={archived ? "gray" : "green"}>
              {archived ? "Archived" : "Active"}
            </NtBadge>
          );
        }
        if (column === "terminatecauseid") {
          return terminationcausedata?.find((each) => each.id == row[column])?.title || row[column];
        }
      }}
    />
  );

  render() {
    const type = this.handleTypeFetch();
    const routeKey = `${this.props.route?.name}/${this.props.route?.params?.type}`;
    const canView = this.handlePermissionCheck(settings.permissionTypeView, routeKey);
    const count = this.state.totalItems || 0;
    const subtitle = `Call detail records for PIN-billed calls · ${count} CDR${count === 1 ? "" : "s"}`;

    const searchBar = (
      <NtSearchBar
        filterCount={this.state.searchFilter ? Object.keys(this.state.searchFilter).length : 0}
        value={this.state.searchText}
        onChangeText={this.handleSearch}
        onFilterPress={() => this.advanceSearchRef?.current?.show()}
      />
    );

    return (
      <NtRootPageContainer
        pageTitle={`${type?.title || ""} CDRs`}
        pageSubtitle={subtitle}
        permission={canView}
      >
        <NtPageSection
          search={searchBar}
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              columns={cdrcsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              overideColumnName={[
                {
                  id: "pin",
                  title: this.props.route?.params?.type === "cabin" ? type?.title : "Pin",
                },
              ]}
              exportFileName="pin-history"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAdvanceSearchForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default PinHistoryController;
